import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const DatasetCatalog = ({ onDatasetSelect, selectedDataset }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortBy, setSortBy] = useState('updated');

  const datasets = [
    {
      id: 'eu-narratives-2024',
      title: 'EU Cross-Border Narrative Analysis 2024',
      description: `Comprehensive dataset containing pseudonymized narrative patterns across 27 EU member states.\nIncludes coordination indicators, temporal analysis, and cross-platform tracking data.\nUpdated monthly with GDPR-compliant privacy controls.`,
      category: 'narratives',
      size: '2.3 GB',
      records: 847293,
      lastUpdated: '2024-09-15',
      accessLevel: 'academic',
      languages: ['EN', 'DE', 'FR', 'ES', 'IT', 'PL'],
      tags: ['cross-border', 'coordination', 'temporal-analysis'],
      downloadCount: 1247,
      citationCount: 23,
      license: 'Academic Research License v2.1',
      methodology: 'DSA-compliant data collection with automated PII removal',
      dataFormat: ['JSON', 'CSV', 'Parquet'],
      isNew: true
    },
    {
      id: 'fimi-threats-q3-2024',
      title: 'FIMI Threat Intelligence Q3 2024',
      description: `Quarterly compilation of Foreign Information Manipulation and Interference patterns.\nIncludes threat actor attribution, campaign timelines, and impact assessment metrics.\nClassified data available for verified institutional researchers.`,
      category: 'threats',
      size: '1.8 GB',
      records: 234567,
      lastUpdated: '2024-09-20',
      accessLevel: 'institutional',
      languages: ['EN', 'RU', 'ZH', 'AR'],
      tags: ['fimi', 'attribution', 'quarterly'],
      downloadCount: 456,
      citationCount: 8,
      license: 'Institutional Access Agreement',
      methodology: 'Multi-source intelligence fusion with confidence scoring',
      dataFormat: ['JSON', 'XML'],
      isClassified: true
    },
    {
      id: 'platform-signals-2024',
      title: 'Platform Signals Aggregation 2024',
      description: `Aggregated signals from major platforms including engagement metrics, content velocity, and virality indicators.\nAnonymized user behavior patterns and content propagation pathways.\nReal-time updates with 24-hour data latency.`,
      category: 'platforms',
      size: '5.7 GB',
      records: 2847392,
      lastUpdated: '2024-09-21',
      accessLevel: 'academic',
      languages: ['Multi-language'],
      tags: ['platforms', 'engagement', 'real-time'],
      downloadCount: 2341,
      citationCount: 45,
      license: 'Open Research License',
      methodology: 'Platform API aggregation with privacy-preserving techniques',
      dataFormat: ['JSON', 'CSV', 'BigQuery'],
      isPopular: true
    },
    {
      id: 'election-monitoring-2024',
      title: 'EU Elections Information Environment 2024',
      description: `Comprehensive monitoring dataset from the 2024 European Parliament elections.\nIncludes candidate mentions, policy narrative tracking, and information quality metrics.\nCross-referenced with official election results and media coverage analysis.`,
      category: 'elections',
      size: '3.2 GB',
      records: 1456789,
      lastUpdated: '2024-07-15',
      accessLevel: 'public',
      languages: ['All EU Languages'],
      tags: ['elections', 'democracy', 'policy-tracking'],
      downloadCount: 3456,
      citationCount: 67,
      license: 'Creative Commons Attribution 4.0',
      methodology: 'Multi-modal content analysis with human verification',
      dataFormat: ['JSON', 'CSV', 'RDF'],
      isComplete: true
    },
    {
      id: 'crisis-response-logs-2024',
      title: 'Crisis Response Coordination Logs 2024',
      description: `Anonymized logs from crisis response coordination activities across EU member states.\nIncludes response times, coordination patterns, and effectiveness metrics.\nSuitable for policy analysis and emergency preparedness research.`,
      category: 'crisis',
      size: '890 MB',
      records: 123456,
      lastUpdated: '2024-09-18',
      accessLevel: 'institutional',
      languages: ['EN', 'Local Languages'],
      tags: ['crisis', 'coordination', 'response-times'],
      downloadCount: 234,
      citationCount: 12,
      license: 'Government Research License',
      methodology: 'Operational data extraction with security classification review',
      dataFormat: ['JSON', 'CSV'],
      isRestricted: true
    },
    {
      id: 'media-literacy-impact-2024',
      title: 'Media Literacy Impact Assessment 2024',
      description: `Longitudinal study data measuring the effectiveness of media literacy interventions.\nIncludes pre/post assessment scores, demographic correlations, and behavioral change indicators.\nAnonymized survey responses from 50,000+ participants across 15 EU countries.`,
      category: 'education',
      size: '1.1 GB',
      records: 567890,
      lastUpdated: '2024-08-30',
      accessLevel: 'academic',
      languages: ['EN', 'DE', 'FR', 'ES', 'NL'],
      tags: ['education', 'impact-assessment', 'longitudinal'],
      downloadCount: 789,
      citationCount: 34,
      license: 'Educational Research License',
      methodology: 'Randomized controlled trial with ethical approval',
      dataFormat: ['SPSS', 'CSV', 'R'],
      isLongitudinal: true
    }
  ];

  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    { value: 'narratives', label: 'Narrative Analysis' },
    { value: 'threats', label: 'Threat Intelligence' },
    { value: 'platforms', label: 'Platform Data' },
    { value: 'elections', label: 'Election Monitoring' },
    { value: 'crisis', label: 'Crisis Response' },
    { value: 'education', label: 'Media Literacy' }
  ];

  const sortOptions = [
    { value: 'updated', label: 'Last Updated' },
    { value: 'popular', label: 'Most Downloaded' },
    { value: 'cited', label: 'Most Cited' },
    { value: 'size', label: 'Dataset Size' },
    { value: 'alphabetical', label: 'Alphabetical' }
  ];

  const accessLevelColors = {
    public: 'bg-success/10 text-success',
    academic: 'bg-primary/10 text-primary',
    institutional: 'bg-warning/10 text-warning'
  };

  const filteredDatasets = datasets?.filter(dataset => {
    const matchesSearch = dataset?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         dataset?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         dataset?.tags?.some(tag => tag?.toLowerCase()?.includes(searchQuery?.toLowerCase()));
    const matchesCategory = filterCategory === 'all' || dataset?.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const sortedDatasets = [...filteredDatasets]?.sort((a, b) => {
    switch (sortBy) {
      case 'updated':
        return new Date(b.lastUpdated) - new Date(a.lastUpdated);
      case 'popular':
        return b?.downloadCount - a?.downloadCount;
      case 'cited':
        return b?.citationCount - a?.citationCount;
      case 'size':
        return parseFloat(b?.size) - parseFloat(a?.size);
      case 'alphabetical':
        return a?.title?.localeCompare(b?.title);
      default:
        return 0;
    }
  });

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-EU')?.format(num);
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-soft">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Dataset Catalog</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {filteredDatasets?.length} datasets available for research
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Icon name="Database" size={20} className="text-primary" />
            <span className="text-sm font-medium text-primary">DSA Compliant</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Input
            type="search"
            placeholder="Search datasets, tags, or descriptions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="md:col-span-1"
          />
          <Select
            options={categoryOptions}
            value={filterCategory}
            onChange={setFilterCategory}
            placeholder="Filter by category"
          />
          <Select
            options={sortOptions}
            value={sortBy}
            onChange={setSortBy}
            placeholder="Sort by"
          />
        </div>
      </div>
      <div className="max-h-96 overflow-y-auto">
        {sortedDatasets?.length === 0 ? (
          <div className="p-8 text-center">
            <Icon name="Search" size={48} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No datasets found</h3>
            <p className="text-muted-foreground">
              Try adjusting your search criteria or category filter
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {sortedDatasets?.map((dataset) => (
              <div
                key={dataset?.id}
                className={`p-6 hover:bg-muted/50 transition-smooth cursor-pointer ${
                  selectedDataset?.id === dataset?.id ? 'bg-primary/5 border-l-4 border-l-primary' : ''
                }`}
                onClick={() => onDatasetSelect(dataset)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h3 className="text-lg font-semibold text-foreground">{dataset?.title}</h3>
                      {dataset?.isNew && (
                        <span className="px-2 py-1 bg-success/10 text-success text-xs font-medium rounded-full">
                          New
                        </span>
                      )}
                      {dataset?.isPopular && (
                        <span className="px-2 py-1 bg-warning/10 text-warning text-xs font-medium rounded-full">
                          Popular
                        </span>
                      )}
                      {dataset?.isClassified && (
                        <span className="px-2 py-1 bg-destructive/10 text-destructive text-xs font-medium rounded-full">
                          Classified
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-3 leading-relaxed whitespace-pre-line">
                      {dataset?.description}
                    </p>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${accessLevelColors?.[dataset?.accessLevel]}`}>
                    {dataset?.accessLevel?.charAt(0)?.toUpperCase() + dataset?.accessLevel?.slice(1)}
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="flex items-center space-x-2">
                    <Icon name="HardDrive" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-foreground font-medium">{dataset?.size}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="FileText" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">{formatNumber(dataset?.records)} records</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Download" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">{formatNumber(dataset?.downloadCount)} downloads</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Quote" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">{dataset?.citationCount} citations</span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      <Icon name="Calendar" size={14} className="text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">
                        Updated {formatDate(dataset?.lastUpdated)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Globe" size={14} className="text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">
                        {Array.isArray(dataset?.languages) ? dataset?.languages?.join(', ') : dataset?.languages}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {dataset?.tags?.slice(0, 3)?.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded-md"
                      >
                        {tag}
                      </span>
                    ))}
                    {dataset?.tags?.length > 3 && (
                      <span className="text-xs text-muted-foreground">
                        +{dataset?.tags?.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DatasetCatalog;